import { bufferFromString, stringFromBuffer, base64urlFromString } from '@rambler-id/crypto';
import randomString from 'random-string';
import CBOR from 'cbor-js';
import { Errors } from './errors';
const PUBLIC_KEY_CREDENTIAL_TYPE = 'public-key';
export const createCredential = async (ticketId, challenge, chainId, displayName = '') => {
    let credential;
    try {
        const creationOptions = {
            publicKey: {
                attestation: 'direct',
                authenticatorSelection: {
                    authenticatorAttachment: 'platform',
                    userVerification: 'required'
                },
                challenge: bufferFromString(window.atob(challenge)),
                pubKeyCredParams: [
                    { type: PUBLIC_KEY_CREDENTIAL_TYPE, alg: -7 },
                    { type: PUBLIC_KEY_CREDENTIAL_TYPE, alg: -35 },
                    { type: PUBLIC_KEY_CREDENTIAL_TYPE, alg: -36 },
                    { type: PUBLIC_KEY_CREDENTIAL_TYPE, alg: -257 },
                    { type: PUBLIC_KEY_CREDENTIAL_TYPE, alg: -258 },
                    { type: PUBLIC_KEY_CREDENTIAL_TYPE, alg: -259 },
                    { type: PUBLIC_KEY_CREDENTIAL_TYPE, alg: -37 },
                    { type: PUBLIC_KEY_CREDENTIAL_TYPE, alg: -38 },
                    { type: PUBLIC_KEY_CREDENTIAL_TYPE, alg: -39 },
                    { type: PUBLIC_KEY_CREDENTIAL_TYPE, alg: -8 }
                ],
                rp: {
                    name: 'Rambler ID',
                    id: 'id.rambler.ru'
                },
                timeout: 60000,
                user: {
                    id: bufferFromString(window.atob(chainId + randomString())),
                    name: displayName,
                    displayName: displayName
                }
            }
        };
        credential = (await navigator.credentials.create(creationOptions));
    }
    catch (error) {
        if (error.code === 0) {
            throw new Error(Errors.CREATION_CANCELLED);
        }
        throw new Error(Errors.CREATION_FAILED);
    }
    return decodeCredential(ticketId, credential);
};
export const decodeCredential = (ticketId, credential) => {
    try {
        const { id, rawId, type, response } = credential;
        const { attestationObject, clientDataJSON } = response;
        const decodedAttestationObject = CBOR.decode(attestationObject);
        decodedAttestationObject.attStmt.sig = base64urlFromString(stringFromBuffer(decodedAttestationObject.attStmt.sig));
        decodedAttestationObject.authData = parseAuthData(decodedAttestationObject.authData);
        const utf8Decoder = window.TextDecoder
            ? new window.TextDecoder('utf-8')
            : // eslint-disable-next-line
                { decode: () => { } };
        const decodedClientDataJSON = JSON.parse(utf8Decoder.decode(clientDataJSON));
        return {
            ticketId,
            attestation: {
                id,
                rawId: base64urlFromString(stringFromBuffer(rawId)),
                type,
                response: {
                    clientDataJson: decodedClientDataJSON,
                    attestationObject: decodedAttestationObject
                }
            }
        };
    }
    catch (error) {
        if (error instanceof Error &&
            error.message === Errors.CREATION_PARSING_FAILED) {
            throw new Error(Errors.CREATION_PARSING_FAILED);
        }
        throw new Error(Errors.CREATION_DECODING_FAILED);
    }
};
export const parseAuthData = (buffer) => {
    try {
        if (buffer.byteLength < 37) {
            throw new Error(Errors.CREATION_PARSING_FAILED);
        }
        const rpIdHash = base64urlFromString(stringFromBuffer(buffer.slice(0, 32)));
        buffer = buffer.slice(32);
        const flagsBuffer = buffer.slice(0, 1);
        const flagsInt = flagsBuffer[0];
        const userPresent = !!(flagsInt & 0x01);
        const userVerified = !!(flagsInt & 0x04);
        const attestedCredentialDataFlag = !!(flagsInt & 0x40);
        const extensionDataIncluded = !!(flagsInt & 0x80);
        const flags = {
            userPresent,
            userVerified,
            attestedCredentialData: attestedCredentialDataFlag,
            extensionDataIncluded,
            flagsInt
        };
        buffer = buffer.slice(1);
        const signCountBuffer = buffer.slice(0, 4);
        const signCountDataView = new DataView(signCountBuffer.buffer);
        const signCount = signCountDataView.getInt32(0);
        buffer = buffer.slice(4);
        let aaguid;
        let credentialId;
        let credentialPublicKey;
        const attestationMinLen = 16 + 2 + 16 + 77;
        if (attestedCredentialDataFlag) {
            if (buffer.byteLength < attestationMinLen) {
                throw new Error(Errors.CREATION_PARSING_FAILED);
            }
            aaguid = base64urlFromString(stringFromBuffer(buffer.slice(0, 16)));
            buffer = buffer.slice(16);
            const credentialIdLengthBuffer = buffer.slice(0, 2);
            buffer = buffer.slice(2);
            const credentialIdLengthDataView = new DataView(credentialIdLengthBuffer.buffer);
            const credentialIdLength = credentialIdLengthDataView.getInt16(0);
            credentialId = base64urlFromString(stringFromBuffer(buffer.slice(0, credentialIdLength)));
            buffer = buffer.slice(credentialIdLength);
            credentialPublicKey = CBOR.decode(buffer.buffer);
            credentialPublicKey[-2] = base64urlFromString(stringFromBuffer(credentialPublicKey[-2]));
            credentialPublicKey[-3] = base64urlFromString(stringFromBuffer(credentialPublicKey[-3]));
            return {
                rpIdHash,
                flags,
                signCount,
                attestedCredentialData: {
                    aaguid,
                    credentialIdLength,
                    credentialId,
                    credentialPublicKey
                }
            };
        }
        else {
            throw new Error(Errors.CREATION_PARSING_FAILED);
        }
    }
    catch (error) {
        throw new Error(Errors.CREATION_PARSING_FAILED);
    }
};
